import { LoadingPage } from 'components/LoadingPage'
import { AuthProvider, useAuthContext } from 'contexts/auth.context'
import React, { lazy, ReactElement, Suspense, useEffect, useState } from 'react'
import { hasAuthParams, useAuth } from 'react-oidc-context'
import { PERMISSOES_ABRIR_TERMO } from 'utils/helpers/termo-entrega-permissoes'
import { Navigate, Route, Routes as Switch, useLocation } from 'react-router-dom'
import {
  Funcionalidades,
  hearingFunctionalities,
  teenagerRecordFunctionalities,
} from 'utils/helpers/funcionalidades'

const WelcomePage = lazy(() => import('pages/Welcome'))
const DashboardPage = lazy(() => import('pages/Dashboard'))
const SearchHearingPage = lazy(() => import('pages/SearchHearing'))
const CreateHearingPage = lazy(() =>
  import('pages/CreateHearing').then((m) => ({ default: m.CreateHearingPage })),
)
const NewHearingRedirect = lazy(() =>
  import('pages/CreateHearing/components/NewHearingRedirect').then((m) => ({
    default: m.NewHearingRedirect,
  })),
)
const DownloadHearingPage = lazy(() => import('pages/DownloadHearing'))
const ViewSummaryHearing = lazy(() => import('pages/ViewSummaryHearing'))
const SearchTeenagerPage = lazy(() => import('pages/SearchTeenager'))
const TeenagersRecordPage = lazy(() =>
  import('pages/TeenagersRecord').then((m) => ({ default: m.TeenagersRecordPage })),
)
const PendingProcessHearing = lazy(() => import('pages/PendingProcessHearing'))
const ViewPendingProcessHearing = lazy(() => import('pages/ViewPendingProcessHearing'))
const TeenagersEditPage = lazy(() =>
  import('pages/TeenagersEdit').then((m) => ({ default: m.TeenagersEditPage })),
)
const CreateTeenagerPage = lazy(() => import('pages/CreateTeenager'))
const TeenagerRecordAuditPage = lazy(() =>
  import('pages/Report/TeenagerRecordAudit').then((m) => ({ default: m.TeenagerRecordAuditPage })),
)
const SearchNotificationsPage = lazy(() =>
  import('pages/SearchNotifications').then((m) => ({ default: m.SearchNotificationsPage })),
)
const SearchCreasPage = lazy(() =>
  import('pages/SearchCreas').then((m) => ({ default: m.SearchCreasPage })),
)
const CreateCreasPage = lazy(() =>
  import('pages/CreateCreas').then((m) => ({ default: m.CreateCreasPage })),
)
const StepGeneralDataPage = lazy(() =>
  import('pages/CreateHearing/components/StepGeneralData').then((m) => ({
    default: m.StepGeneralDataPage,
  })),
)
const StepIdentificationPage = lazy(() =>
  import('pages/CreateHearing/components/StepIdentification').then((m) => ({
    default: m.StepIdentificationPage,
  })),
)
const StepSummaryPage = lazy(() =>
  import('pages/CreateHearing/components/StepSummary').then((m) => ({
    default: m.StepSummaryPage,
  })),
)
const StepNarrativesPage = lazy(() =>
  import('pages/CreateHearing/components/StepNarratives').then((m) => ({
    default: m.StepNarrativesPage,
  })),
)
const StepCircumstancesPage = lazy(() =>
  import('pages/CreateHearing/components/StepCircumstances').then((m) => ({
    default: m.StepCircumstancesPage,
  })),
)
const SocioeducationMeasureEditPage = lazy(() =>
  import('pages/SocioeducationMeasureEdit').then((m) => ({
    default: m.SocioeducationMeasureEditPage,
  })),
)
const SocioeducationMeasureIdentificationEdit = lazy(() =>
  import('pages/SocioeducationMeasureEdit/Identification').then((m) => ({
    default: m.SocioeducationMeasureIdentificationEdit,
  })),
)
const FamilyDataPage = lazy(() =>
  import('pages/SocioeducationMeasureEdit/FamilyData').then((m) => ({ default: m.FamilyDataPage })),
)
const MeioAbertoAcompanhamentoPage = lazy(() =>
  import('pages/SocioeducationMeasureEdit/Accompaniment').then((m) => ({
    default: m.MeioAbertoAcompanhamentoPage,
  })),
)
const MeioAbertoPlanejamentoPage = lazy(() =>
  import('pages/SocioeducationMeasureEdit/Planejamento').then((m) => ({
    default: m.MeioAbertoPlanejamentoPage,
  })),
)
// Passo 7 — Avaliação do Técnico de Referência e Técnico de Apoio (HU 854293).
const MeioAbertoAvaliacaoTecnicaPage = lazy(() =>
  import('pages/SocioeducationMeasureEdit/AvaliacaoTecnica').then((m) => ({
    default: m.MeioAbertoAvaliacaoTecnicaPage,
  })),
)
const MeasuresAppliedPage = lazy(() => import('pages/SocioeducationMeasureEdit/MeasuresApplied'))
const ProceduralDataPage = lazy(() =>
  import('pages/SocioeducationMeasureEdit/ProceduralData').then((m) => ({
    default: m.ProceduralDataPage,
  })),
)
const ConfigurarCSI = lazy(() => import('pages/ConfigureCSI'))
const ConsultarCSI = lazy(() => import('pages/SearchCSIComunication'))
const CadastrarCSI = lazy(() => import('pages/CSIComunication'))
const TermoEntregaExpedirPage = lazy(() => import('pages/HearingDeliveryTerm/Expedir'))
const TermoEntregaImpressaoPage = lazy(() => import('pages/HearingDeliveryTerm/Termo'))
const DownloadTeenagerPage = lazy(() => import('pages/DownloadTeenager'))
const FaleConoscoPage = lazy(() => import('pages/FaleConosco'))

type ProtectedRouteProps = {
  children: ReactElement
  isAuthenticated: boolean
  /** Uma permissão, ou uma lista em que basta ter qualquer uma. */
  permission: string | string[]
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  isAuthenticated,
  permission,
}) => {
  const location = useLocation()
  const { handleHasPermission, isLoading } = useAuthContext()

  const userHasRequiredHole = Array.isArray(permission)
    ? permission.some(handleHasPermission)
    : handleHasPermission(permission)

  if (isAuthenticated && !userHasRequiredHole && !isLoading) {
    return <Navigate to='/' state={{ from: location }} />
  }

  return children
}

export const Routes: React.FC = () => {
  const auth = useAuth()
  const { handleHasPermission } = useAuthContext()

  const [hasTriedSigning, setHasTriedSigning] = useState<boolean>(false)

  useEffect(() => {
    const signInIfNecessary = async () => {
      try {
        if (
          !hasAuthParams() &&
          !auth.isAuthenticated &&
          !auth.activeNavigator &&
          !auth.isLoading &&
          !hasTriedSigning
        ) {
          await auth.signinRedirect()
          setHasTriedSigning(true)
        }
      } catch (error) {
        console.error('Error during signinRedirect:', error)
      }
    }

    signInIfNecessary()
  }, [auth, hasTriedSigning])

  if (auth.isLoading) {
    return <LoadingPage />
  }

  if (auth.error) {
    return <div>Oops... {auth.error.message}</div>
  }

  return (
    <AuthProvider>
      <Suspense fallback={<LoadingPage />}>
        <Switch>
          <Route path='/' element={<WelcomePage />} />
          <Route
            path='/painel'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.painel.consultar}
              >
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          {/* HU 865155 — aberta a qualquer usuário autenticado (sem permissão específica). */}
          <Route path='/fale-conosco' element={<FaleConoscoPage />} />
          <Route
            path='/consultar-oitiva'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={hearingFunctionalities.consultar}
              >
                <SearchHearingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/nova-oitiva'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={hearingFunctionalities.cadastrar}
              >
                <NewHearingRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path='/nova-oitiva/:id'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={hearingFunctionalities.cadastrar}
              >
                <CreateHearingPage />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to='dados-gerais' replace />} />
            <Route path='dados-gerais' element={<StepGeneralDataPage />} />
            <Route path='identificacao' element={<StepIdentificationPage />} />
            <Route path='circunstancias' element={<StepCircumstancesPage />} />
            <Route path='narrativas' element={<StepNarrativesPage />} />
            <Route path='resumo' element={<StepSummaryPage />} />
          </Route>
          <Route
            path='/download-oitiva/:id?'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={hearingFunctionalities.download}
              >
                <DownloadHearingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/visualizar-resumo-oitiva/:id?'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.oitiva.visualizar}
              >
                <ViewSummaryHearing />
              </ProtectedRoute>
            }
          />
          <Route path='/oitiva/:id/termo-entrega'>
            <Route
              index
              element={
                <ProtectedRoute
                  isAuthenticated={auth.isAuthenticated}
                  permission={hearingFunctionalities.consultar}
                >
                  <TermoEntregaImpressaoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='expedir'
              element={
                <ProtectedRoute
                  isAuthenticated={auth.isAuthenticated}
                  permission={PERMISSOES_ABRIR_TERMO}
                >
                  <TermoEntregaExpedirPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path='/consultar-adolescente'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={teenagerRecordFunctionalities.consultar}
              >
                <SearchTeenagerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/cadastrar-adolescente'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.fichaAdolescente.cadastrar}
              >
                <CreateTeenagerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/ficha-adolescente/:id?'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={teenagerRecordFunctionalities.visualizar}
              >
                <TeenagersRecordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/ficha-adolescente/:id?/imprimir'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={teenagerRecordFunctionalities.imprimir}
              >
                <DownloadTeenagerPage
                  isPrint={handleHasPermission(teenagerRecordFunctionalities.imprimir)}
                  isExport={false}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path='/ficha-adolescente/:id?/exportar'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={teenagerRecordFunctionalities.exportar}
              >
                <DownloadTeenagerPage
                  isPrint={false}
                  isExport={handleHasPermission(teenagerRecordFunctionalities.exportar)}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path='/ficha-adolescente/:id/editar'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={teenagerRecordFunctionalities.editar}
              >
                <TeenagersEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path='/processos-pendentes'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.integraPolicial.consultar}
              >
                <PendingProcessHearing />
              </ProtectedRoute>
            }
          />
          <Route
            path='/processos-pendentes/:pendingProcessHearingId'
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.integraPolicial.visualizar}
              >
                <ViewPendingProcessHearing />
              </ProtectedRoute>
            }
          />
          <Route
            path={'/auditoria/ficha-adolescente'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.relatorio.auditoria.fichaAdolescente.consultar}
              >
                <TeenagerRecordAuditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={'/notificacoes'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.notificacoes.consultar}
              >
                <SearchNotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={'/creas'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.creas.consultar}
              >
                <SearchCreasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={'/novo-creas'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.creas.cadastrar}
              >
                <CreateCreasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={'/novo-creas/:id'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.creas.editar}
              >
                <CreateCreasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={'/meio-aberto/:id/'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.meioAberto.visualizar}
              >
                <SocioeducationMeasureEditPage />
              </ProtectedRoute>
            }
          >
            <Route path={'identificacao'} element={<SocioeducationMeasureIdentificationEdit />} />
            <Route path={'dados-familiares'} element={<FamilyDataPage />} />
            <Route path={'dados-processuais'} element={<ProceduralDataPage />} />
            <Route path={'medidas-aplicadas'} element={<MeasuresAppliedPage />} />
            <Route path={'acompanhamento'} element={<MeioAbertoAcompanhamentoPage />} />
            {/* Passo 6 — Planejamento do Acompanhamento (PIA). Stories #823184/#823279. */}
            <Route path={'planejamento'} element={<MeioAbertoPlanejamentoPage />} />
            {/* Passo 7 — Avaliação do Técnico de Referência e Técnico de Apoio. HU 854293. */}
            <Route path={'avaliacao-tecnica'} element={<MeioAbertoAvaliacaoTecnicaPage />} />
          </Route>
          <Route
            path={'configurar-csi'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.comunicacaoCSI.configurar}
              >
                <ConfigurarCSI />
              </ProtectedRoute>
            }
          />
          <Route
            path={'consultar-csi'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.comunicacaoCSI.consultar}
              >
                <ConsultarCSI />
              </ProtectedRoute>
            }
          />
          <Route
            path={'cadastrar-csi'}
            element={
              <ProtectedRoute
                isAuthenticated={auth.isAuthenticated}
                permission={Funcionalidades.comunicacaoCSI.cadastrar}
              >
                <CadastrarCSI />
              </ProtectedRoute>
            }
          />
        </Switch>
      </Suspense>
    </AuthProvider>
  )
}

export default Routes
